import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type {
  SHARED_CONSTANTS_DOMAIN_STATUS,
  SHARED_CONSTANTS_EMAIL_SELF_SERVE_ERRORS,
  SHARED_CONSTANTS_PREREQUISITE_STATUS,
} from "@lib/shared_constants";

export type DomainStatus = keyof SHARED_CONSTANTS_DOMAIN_STATUS;

export type PrerequisiteStatus = keyof SHARED_CONSTANTS_PREREQUISITE_STATUS;

export interface SendingDomain {
  id: string;
  domain: string;
  status: DomainStatus;
  appGroupIds: string[];
  autoAddedToAppGroups: boolean;
  mailFromDomain?: string;
  /**
   * SES configuration set name backing this sending domain. Two sending domains in the same
   * configuration set share its single tracking domain. Optional for backward compatibility with
   * older API responses that omit this field. Used by the frontend to resolve the effective
   * tracking domain for a sending domain.
   */
  configurationSetName?: string;
}

export interface TrackingDomain {
  parentId: string;
  domain: string;
  status: DomainStatus;
}

export interface IpPool {
  name: string;
  ipAddresses: string[];
  sendingDomains: SendingDomain[];
  trackingDomains: TrackingDomain[];
}

export interface DelegatedDomain {
  id: string;
  domain: string;
}

export type ConfigurationData =
  | { status: SHARED_CONSTANTS_PREREQUISITE_STATUS["ADD_IP_POOL"] }
  | { status: SHARED_CONSTANTS_PREREQUISITE_STATUS["ADD_IP_ADDRESSES"] }
  | { status: SHARED_CONSTANTS_PREREQUISITE_STATUS["ADD_DELEGATED_DOMAIN"] }
  | {
      status: SHARED_CONSTANTS_PREREQUISITE_STATUS["COMPLETED"];
      ipPools: IpPool[];
      delegatedDomains: DelegatedDomain[];
    };

export interface GetConfigurationDataResult {
  data: ConfigurationData;
}

export type NoSesAccountError = FetchUserEncodedError<SHARED_CONSTANTS_EMAIL_SELF_SERVE_ERRORS["NO_SES_ACCOUNT"]>;

export type MultipleSesAccountsError = FetchUserEncodedError<
  SHARED_CONSTANTS_EMAIL_SELF_SERVE_ERRORS["MULTIPLE_SES_ACCOUNTS"]
>;

export type GetConfigurationDataError = NoSesAccountError | MultipleSesAccountsError;

export const isGetConfigurationDataError = (error: unknown): error is GetConfigurationDataError =>
  error instanceof FetchUserEncodedError;
